import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

import { computed } from 'vue';
import { inject } from 'vue';
import { isFunction } from 'lodash';
import { onBeforeMount } from 'vue';
import { onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { watch } from 'vue';
//--- Function imports

//### Service imports
import AccountService from '@/account/account.service';
//--- Service imports

//### Model imports
//--- Model imports

//### Component imports
import { SidebarMenu } from 'vue-sidebar-menu';
//--- Component imports

//### External Properties
//--- External Properties

//### Setup

export default /*#__PURE__*/_defineComponent({
  __name: 'sidebar',
  setup(__props) {

//### Function imports
const accountService = inject<AccountService>('accountService');

const i18n = useI18n();
const route = useRoute();
const store = useStore();
//--- Setup

//### Store/Reactive Data
const isInImpersonateMode = computed(() => store.getters.isInImpersonateMode);
const isIphone = computed(() => store.getters.isIphone);
const isPhoneDevice = computed(() => store.getters.isPhoneDevice);
const isTabletDevice = computed(() => store.getters.isTabletDevice);
const menuExpanded = computed(() => store.getters.menuExpanded);

watch(route, () => {
  getRouteAndCreateMenu();
});
//--- Store/Reactive Data

//### Variables const let
//--- Variables const let

//### Component Lifecycle
onBeforeMount(() => {
  closeMenuIfMobileDevice(null, null);
});

onBeforeUnmount(() => {
  store.commit('toggleMenu', true);
});
//--- Component Lifecycle

//### Functions
function getRouteAndCreateMenu() {
  const menu = [];
  const authorities = store.getters.currentUser?.authorities;

  if (authorities) {
    if (accountService.hasRole('SUPERADMIN')) {
      menu.push({
        title: i18n.t('menu.admin.main'),
        icon: 'fa fa-toolbox',
        child: [
          {
            href: '/admin/tenant-management',
            icon: 'fa fa-users',
            title: i18n.t('menu.admin.tenantManagement'),
          },
          {
            href: '/admin/gateway',
            icon: 'fa fa-network-wired',
            title: i18n.t('menu.admin.gateway'),
          },
          {
            href: '/admin/metrics',
            icon: 'fa fa-chart-bar',
            title: i18n.t('menu.admin.metrics'),
          },
          {
            href: '/admin/health',
            icon: 'fa fa-house-chimney-medical',
            title: i18n.t('menu.admin.health'),
          },
          {
            href: '/admin/apidocs',
            icon: 'fa fa-hashtag',
            title: i18n.t('menu.admin.apidocs'),
          },
          {
            href: '/admin/configuration',
            icon: 'fa fa-gears',
            title: i18n.t('menu.admin.configuration'),
          },
        ],
      });
    }

    menu.push({
      href: '/dashboard',
      title: i18n.t('menu.dashboard'),
      icon: 'fa fa-chart-line',
    });

    if (accountService.hasRole('SUPERADMIN')) {
      menu.push({
        href: '/billing',
        title: i18n.t('menu.billing'),
        icon: 'fa fa-dollar-sign',
      });
    }

    menu.push({
      href: '/template',
      title: i18n.t('menu.templates'),
      icon: 'fa fa-tags',
    });

    const notification = {
      title: i18n.t('menu.notifications'),
      icon: 'fa fa-comment',
      child: [
        {
          href: '/notification',
          title: i18n.t('menu.notificationsOverview'),
          icon: 'fa fa-message',
        },
      ],
    };

    if (accountService.hasAnyRole(['ROLE_SUPERADMIN', 'ROLE_ADMIN'])) {
      notification.child.push(
        {
          href: '/notification-send',
          title: i18n.t('menu.notificationsSend'),
          icon: 'fa fa-comment-medical',
        },
        {
          href: '/notification-scheduled',
          title: i18n.t('menu.notificationsScheduled'),
          icon: 'fa fa-comment-dots',
        },
      );
    }

    menu.push(notification);

    menu.push({
      href: '/customers',
      title: i18n.t('menu.customers'),
      icon: 'fa fa-children',
    });

    menu.push({
      href: '/pass-list',
      title: i18n.t('menu.pass-list'),
      icon: 'fa fa-address-card',
    });

    if (accountService.hasRole('ADMIN')) {
      menu.push({
        href: '/statistics',
        title: i18n.t('menu.statistics'),
        icon: 'fa fa-chart-column',
      });
    }

    if (accountService.hasAnyRole(['ROLE_SUPERADMIN', 'ROLE_ADMIN'])) {
      menu.push({
        title: i18n.t('menu.settings'),
        icon: 'fa fa-gear',
        child: [
          {
            href: '/admin/user-management',
            icon: 'fa fa-user',
            title: i18n.t('menu.admin.userManagement'),
          },
          {
            href: '/settings/company',
            title: i18n.t('menu.settingCompany'),
            icon: 'fa-solid fa-building',
          },
          {
            href: '/settings/payment-methods',
            title: i18n.t('menu.settingPaymentMethods'),
            icon: 'fa-solid fa-credit-card ',
          },
          {
            href: '/settings/subscription',
            title: i18n.t('menu.settingSubscriptions'),
            icon: 'fa-solid fa-cube',
          },
          {
            href: '/settings/program',
            title: i18n.t('menu.settingProgram'),
            icon: 'fa-solid fa-gear',
          },
        ],
      });
    } else if (accountService.hasRole('USER')) {
      menu.push({
        title: i18n.t('menu.settings'),
        icon: 'fa fa-gear',
        child: [
          {
            href: '/settings/program',
            title: i18n.t('menu.settingProgram'),
            icon: 'fa-solid fa-gear',
          },
        ],
      });
    }
  }
  return menu;
}

function toggleMenu() {
  store.commit('toggleMenu', !menuExpanded.value);
}

function closeMenuIfMobileDevice(event, item) {
  if ((isPhoneDevice.value || isTabletDevice.value) && (!item || (item && !item.child))) {
    store.commit('toggleMenu', false);
  }
}

function touchHandler(event) {
  // Workaround: on mobile device this will cause a double click on menu item !
  if (isIphone.value && (isPhoneDevice.value || isTabletDevice.value)) {
    if (isFunction(event.target.click)) {
      event.target.click();
    }
  }
}

//--- Functions

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!
  const _directive_touch = _resolveDirective("touch")!

  return (_openBlock(), _createElementBlock("div", null, [
    _withDirectives((_openBlock(), _createBlock(_unref(SidebarMenu), {
      onItemClick: _cache[1] || (_cache[1] = (event, item) => closeMenuIfMobileDevice(event, item)),
      "onUpdate:collapsed": toggleMenu,
      collapsed: !menuExpanded.value,
      hideToggle: true,
      menu: getRouteAndCreateMenu(),
      showOneChild: true,
      width: isPhoneDevice.value || isTabletDevice.value ? '100%' : '290px',
      class: _normalizeClass(`${isInImpersonateMode.value ? 'impersonate-mode' : ''}`)
    }, {
      header: _withCtx(() => [
        _createVNode(_component_Button, {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleMenu())),
          icon: "fa fa-bars",
          style: {"border":"none","width":"48px","height":"48px","margin":"0.2rem","margin-bottom":"1rem"},
          outlined: "",
          class: "menu-toggler"
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["collapsed", "menu", "width", "class"])), [
      [_directive_touch, touchHandler]
    ])
  ]))
}
}

})